import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { Check } from "lucide-react";
import { cn } from "@nosible/shared/lib/utils";
import { formatPrice } from "@nosible/shared/lib";

export type StdPlan = {
  name: string;
  description?: string | null;
  monthlyPriceCents: number;
  keyFeatures: string[];
};

export type CustomPlan = {
  name: string;
  description?: string | null;
};

type BasePlanCardProps = {
  icon: ReactNode;
  iconClassName: string;
  colorClassName?: string;
  isPopular?: boolean;
  className?: string;
  headerBadge?: ReactNode;
};

type StdPlanCardProps = BasePlanCardProps & {
  plan: StdPlan;
  actionLabel: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  actionClassName?: string;
  actionVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "outlineClipped" | "filter" | "filterSelected";
  showMonthlySuffix?: boolean;
};

type CustomPlanCardProps = {
  plan: CustomPlan;
  contactHref: string;
  contactLabel?: string;
};

export function StdPlanCard({
  plan,
  icon,
  iconClassName,
  colorClassName,
  isPopular = false,
  className,
  headerBadge,
  actionLabel,
  onAction,
  actionDisabled = false,
  actionClassName,
  actionVariant,
  showMonthlySuffix = true,
}: StdPlanCardProps) {
  return (
    <Card
      className={cn(
        "relative flex flex-col transition-all hover:scale-[1.02]",
        colorClassName,
        className,
      )}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-neon-green text-obsidian">Most Popular</Badge>
        </div>
      )}

      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", iconClassName)}>{icon}</div>
          <div>
            <CardTitle className="capitalize text-silica">{plan.name}</CardTitle>
            {headerBadge}
          </div>
        </div>
        <CardDescription className="mt-3 [&_a]:text-neon-green [&_a]:hover:underline [&_strong]:text-silica [&_ul]:mt-2 [&_li]:text-sm [&_li]:text-chromium-mist [&_li]:mb-1">
          {plan.description ? `${plan.description}` : `${plan.name} plan with enhanced features`}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1">
        <div className="mb-6">
          <span className="text-4xl font-bold text-silica">{formatPrice(plan.monthlyPriceCents)}</span>
          {showMonthlySuffix && plan.monthlyPriceCents !== -1 && (
            <span className="text-chromium-mist ml-1">/month</span>
          )}
        </div>

        <ul className="space-y-3">
          {plan.keyFeatures.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-neon-green shrink-0" />
              <span className="text-silica">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="pt-4">
        <Button
          className={cn("w-full", actionClassName)}
          variant={actionVariant}
          onClick={onAction}
          disabled={actionDisabled}
        >
          {actionLabel}
        </Button>
      </CardFooter>
    </Card>
  );
}

export function CustomPlanCard({
  plan,
  contactHref,
  contactLabel = "Contact us",
}: CustomPlanCardProps) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-silica">{plan.name}</CardTitle>
        <CardDescription className="[&_a]:text-neon-green [&_a]:hover:underline">
          {plan.description ? `${plan.description}` : `${plan.name} plan with enhanced features`}
        </CardDescription>
      </CardHeader>

      <CardFooter className="mt-auto">
        <Button asChild className="w-full bg-neon-green text-obsidian hover:bg-neon-green/90">
          <a href={contactHref}>{contactLabel}</a>
        </Button>
      </CardFooter>
    </Card>
  );
}
